import React from 'react';
import { IconButton, Tooltip } from '@mui/material';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import PowerSettingsNewOutlinedIcon from '@mui/icons-material/PowerSettingsNewOutlined';
import RestoreOutlinedIcon from '@mui/icons-material/RestoreOutlined';
import PhoneIphoneOutlinedIcon from '@mui/icons-material/PhoneIphoneOutlined';
import MailOutlineRoundedIcon from '@mui/icons-material/MailOutlineRounded';
import BadgeOutlinedIcon from '@mui/icons-material/BadgeOutlined';
import DrawOutlinedIcon from '@mui/icons-material/DrawOutlined';
import PublicOutlinedIcon from '@mui/icons-material/PublicOutlined';
import MeetingRoomOutlinedIcon from '@mui/icons-material/MeetingRoomOutlined';
import ScheduleOutlinedIcon from '@mui/icons-material/ScheduleOutlined';
import { formatResourcePhone, hasSignature, resourceInitials } from './resourceFormat';
import { describeResourceSchedule } from '../../../utils/resourceSchedule';
import styles from './ResourceListCard.module.css';

/** Ação compacta da linha: ícone com tooltip (o span mantém o tooltip no botão desligado). */
function RowAction({ title, label, disabled, onClick, Icon, tone }) {
    return (
        <Tooltip title={title} arrow>
            <span>
                <IconButton
                    size="small"
                    className={`${styles.rowAction} ${tone ? styles[tone] : ''}`}
                    onClick={onClick}
                    disabled={disabled}
                    aria-label={label}
                >
                    <Icon fontSize="small" />
                </IconButton>
            </span>
        </Tooltip>
    );
}

/**
 * Uma agenda: avatar, nome e especialidade, contato que recebe a agenda do dia,
 * registro e assinatura, selos (sala padrão, horário próprio, agendamento online, inativa) e as ações da linha.
 */
function ResourceRow({ resource, signatureSrc, defaultRoomLabel, canManage, reactivateHint, onEdit, onInactivate, onReactivate }) {
    const inactive = resource.Active == 0;
    const phone = formatResourcePhone(resource.Phone, resource.countryCode);
    const ownSchedule = describeResourceSchedule(resource.Schedule);
    const denied = (verb) => `Apenas proprietários e administradores podem ${verb} agendas`;

    return (
        <li className={`${styles.row} ${inactive ? styles.muted : ''}`}>
            <span className={`${styles.avatar} ${inactive ? styles.avatarMuted : ''}`}>
                {resourceInitials(resource.Label)}
            </span>

            <div className={styles.identity}>
                <span className={styles.name}>
                    <span className={styles.ellipsis}>{resource.Label}</span>
                </span>
                <span className={`${styles.secondary} ${styles.ellipsis}`}>
                    {resource.Expertise || 'Sem especialidade'}
                </span>
            </div>

            <div className={styles.detailCell}>
                {phone || resource.Email ? (
                    <>
                        {phone && (
                            <span className={styles.detail}>
                                <PhoneIphoneOutlinedIcon className={styles.detailIcon} />
                                <span className={styles.ellipsis}>{phone}</span>
                            </span>
                        )}
                        {resource.Email && (
                            <span className={styles.detail}>
                                <MailOutlineRoundedIcon className={styles.detailIcon} />
                                <span className={styles.ellipsis}>{resource.Email}</span>
                            </span>
                        )}
                    </>
                ) : (
                    <span className={styles.placeholder}>Sem contato</span>
                )}
            </div>

            <div className={styles.detailCell}>
                <span className={styles.detail}>
                    <BadgeOutlinedIcon className={styles.detailIcon} />
                    {resource.Regulator
                        ? <span className={styles.ellipsis}>{resource.Regulator}</span>
                        : <span className={styles.placeholder}>Sem registro</span>}
                </span>
                {hasSignature(resource) ? (
                    <Tooltip title="Assinatura" arrow>
                        <img src={signatureSrc} alt={`Assinatura de ${resource.Label}`} className={styles.signatureThumb} />
                    </Tooltip>
                ) : (
                    <span className={styles.detail}>
                        <DrawOutlinedIcon className={styles.detailIcon} />
                        <span className={styles.placeholder}>Sem assinatura</span>
                    </span>
                )}
            </div>

            <div className={styles.tags}>
                {inactive && <span className={`${styles.tag} ${styles.tagInactive}`}>Inativa</span>}
                {defaultRoomLabel ? (
                    <Tooltip title="Sala padrão: vem preenchida em cada agendamento novo" arrow>
                        <span className={`${styles.tag} ${styles.tagRoom}`}>
                            <MeetingRoomOutlinedIcon className={styles.tagIcon} />
                            {defaultRoomLabel}
                        </span>
                    </Tooltip>
                ) : null}
                {ownSchedule ? (
                    <Tooltip title={ownSchedule.lines.map((line) => <div key={line}>{line}</div>)} arrow>
                        <span className={`${styles.tag} ${styles.tagRoom}`}>
                            <ScheduleOutlinedIcon className={styles.tagIcon} />
                            {ownSchedule.label}
                        </span>
                    </Tooltip>
                ) : null}
                {resource.public_schedule_enabled ? (
                    <Tooltip title="Habilitada no agendamento público" arrow>
                        <span className={`${styles.tag} ${styles.tagOn}`}>
                            <PublicOutlinedIcon className={styles.tagIcon} />
                            Agendamento público
                        </span>
                    </Tooltip>
                ) : null}
            </div>

            <span className={styles.actions}>
                {inactive ? (
                    <RowAction
                        title={!canManage ? denied('reativar') : (reactivateHint || 'Reativar agenda')}
                        label={`Reativar ${resource.Label}`}
                        disabled={!canManage}
                        onClick={() => onReactivate(resource)}
                        Icon={RestoreOutlinedIcon}
                        tone="positive"
                    />
                ) : (
                    <>
                        <RowAction
                            title={!canManage ? denied('editar') : 'Editar agenda'}
                            label={`Editar ${resource.Label}`}
                            disabled={!canManage}
                            onClick={() => onEdit(resource)}
                            Icon={EditOutlinedIcon}
                        />
                        <RowAction
                            title={!canManage ? denied('inativar') : 'Inativar agenda'}
                            label={`Inativar ${resource.Label}`}
                            disabled={!canManage}
                            onClick={() => onInactivate(resource)}
                            Icon={PowerSettingsNewOutlinedIcon}
                            tone="danger"
                        />
                    </>
                )}
            </span>
        </li>
    );
}

export default ResourceRow;
